/**
 * Layout for /admin/models/:modelId/*.
 * One unified entity (status drives the available tabs and behaviour).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  UserCircle,
  ChartLineUp,
  Calendar,
  LinkSimple,
  Receipt,
  NotePencil,
  WarningCircle,
  Headset,
  GraduationCap,
} from "@phosphor-icons/react";
import { modelsAdminApi } from "../../../api/models";

const ALL_TABS = [
  { key: "profile", icon: UserCircle, always: true },
  { key: "formation", icon: GraduationCap, requiresAccount: true },
  { key: "stats", icon: ChartLineUp, requiresAccount: true },
  { key: "planning", icon: Calendar, requiresAccount: true },
  { key: "adonislink", icon: LinkSimple, requiresAccount: true },
  { key: "billing", icon: Receipt, requiresAccount: true },
  { key: "notes", icon: NotePencil, always: true },
  { key: "leaks", icon: WarningCircle, requiresAccount: true },
  { key: "support", icon: Headset, requiresAccount: true },
];

const STATUS_LABEL = (t, status) =>
  t(`models.status.${status}`, { defaultValue: status });

export default function AdminModelLayout() {
  const { t } = useTranslation();
  const { modelId } = useParams();
  const [model, setModel] = useState(null);

  const reload = useCallback(async () => {
    try {
      setModel(await modelsAdminApi.get(modelId));
    } catch {
      /* ignore */
    }
  }, [modelId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const hasAccount = !!model?.user_id;
  const visibleTabs = ALL_TABS.filter((t) => t.always || hasAccount);
  const basePath = `/admin/models/${modelId}`;
  const photoUrl = model?.photo_id
    ? modelsAdminApi.photoUrl(model.photo_id)
    : null;

  return (
    <div data-testid="admin-model-layout" className="space-y-8">
      <Link
        to="/admin/models"
        data-testid="admin-model-back"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} weight="bold" />
        {t("admin.nav.models")}
      </Link>

      <header className="flex items-center gap-4">
        <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 overflow-hidden">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <UserCircle size={40} weight="light" className="text-slate-400" />
          )}
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {model ? STATUS_LABEL(t, model.status) : "…"}
          </p>
          <h1 className="mt-1 font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            {model
              ? `${model.first_name || ""} ${model.last_name || ""}`.trim() ||
                model.email ||
                "—"
              : "…"}
          </h1>
          {model?.handle && <p className="text-sm text-slate-500">@{model.handle}</p>}
        </div>
      </header>

      <nav
        data-testid="admin-model-tabs"
        className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full"
      >
        {visibleTabs.map((tab) => (
          <NavLink
            key={tab.key}
            to={`${basePath}/${tab.key}`}
            data-testid={`admin-model-tab-${tab.key}`}
            className={({ isActive }) =>
              `relative inline-flex items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${
                isActive
                  ? "text-slate-900"
                  : "text-slate-500 hover:text-slate-900"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <tab.icon size={15} weight={isActive ? "fill" : "regular"} />
                {t(`models.tabs.${tab.key}`)}
                {isActive && (
                  <span className="absolute inset-x-3 bottom-0 h-0.5 bg-slate-900" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ modelId, model, reload }} />
    </div>
  );
}
